import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';

import { getQueryKey } from './useQueryIssueNotificationSettings';

import type { User } from '../useIssue';
import type { IssueNotificationSettings } from './types';

const MUTATION_KEY = 'mutate-issue-notification-settings';

function useMutationIssueNotificationSettings(
  args: { integrationId: number },
  options?: UseMutationOptions<
    void,
    Error,
    { ccContact: Pick<User, 'id' | 'email'>; shouldNotifyDeveloper: boolean },
    { issueNotificationSettings: IssueNotificationSettings }
  >
) {
  const { integrationId } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey(args);

  const mutationKey = MUTATION_KEY;
  const url = new URL(
    `/api/platform/cli/apps/${encodeURIComponent(
      integrationId
    )}/notification-settings`,
    ZAPIER_APP_BASE_URL
  );

  const mutation = useMutation({
    mutationKey,
    async mutationFn({ ccContact, shouldNotifyDeveloper }) {
      const body = JSON.stringify({
        ccContact,
        should_notify: shouldNotifyDeveloper,
      });
      fetchJson(url.href, { body, method: 'PATCH' });
    },
    onMutate: async ({ ccContact, shouldNotifyDeveloper }) => {
      // Optimistic update.
      await queryClient.cancelQueries(queryKey);
      const issueNotificationSettings = queryClient.getQueryData<
        IssueNotificationSettings
      >(queryKey);
      const issueNotificationSettingsNext = {
        ...issueNotificationSettings,
        ccContact,
        shouldNotifyDeveloper,
      };
      queryClient.setQueryData(queryKey, issueNotificationSettingsNext);
      return { issueNotificationSettings };
    },
    onError: (error: any, _variables, context) => {
      queryClient.setQueryData(queryKey, context.issueNotificationSettings);
      notify.failure(error.message, mutationKey);
    },
    onSuccess: () =>
      notify.success('Successfully updated notification settings.'),
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationIssueNotificationSettings;
